import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from "@mui/material";
import { RichTreeView, TreeViewBaseItem } from "@mui/x-tree-view";
import { FC, useEffect, useMemo, useState } from "react";
import getBackendURL from "@/api/api";
import { request } from "@/request";

type County = { geoid: string; name: string; district: string };

type Props = {
	open: boolean;
	onClose: () => void;
	geoids: string[];
	onSave: (geoids: string[]) => void;
};

const DISTRICT_PREFIX = "district:";

// One item per district with its counties as children. `counties` arrive
// sorted by district, so each district's counties are contiguous.
function toTreeItems(counties: County[]): TreeViewBaseItem[] {
	const districts: TreeViewBaseItem[] = [];
	for (const county of counties) {
		const last = districts[districts.length - 1];
		if (!last || last.label !== county.district) {
			districts.push({ id: DISTRICT_PREFIX + county.district, label: county.district, children: [] });
		}
		districts[districts.length - 1].children!.push({ id: county.geoid, label: county.name });
	}
	return districts;
}

// The tree only shows a district as selected when its id is in the selection,
// so add each district whose counties are all among `geoids`
function toSelection(geoids: string[], districts: TreeViewBaseItem[]): string[] {
	const fullDistricts = districts.filter((district) =>
		district.children!.every((county) => geoids.includes(county.id)),
	);
	return [...geoids, ...fullDistricts.map((district) => district.id)];
}

export const CountyPicker: FC<Props> = ({ open, onClose, geoids, onSave }) => {
	const [counties, setCounties] = useState<County[]>([]);
	const [selected, setSelected] = useState<string[]>([]);
	const items = useMemo(() => toTreeItems(counties), [counties]);

	useEffect(() => {
		if (!open) return;
		setSelected(geoids);
		if (counties.length === 0) {
			request(getBackendURL("/esr/counties/")).then(({ data }) => setCounties(data));
		}
	}, [open]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Specify by County</DialogTitle>
			<DialogContent>
				<RichTreeView
					items={items}
					multiSelect
					checkboxSelection
					selectionPropagation={{ descendants: true, parents: true }}
					selectedItems={toSelection(selected, items)}
					onSelectedItemsChange={(_event, ids) =>
						setSelected(ids.filter((id) => !id.startsWith(DISTRICT_PREFIX)))
					}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant="contained"
					onClick={() => {
						onSave(selected);
						onClose();
					}}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
